import {
    ChartDataStatus,
    chartsDataStatusMessageType,
    isDashboardEmpty,
    parseChartsDataStatusMessage,
} from "src/components/Superset/isDashboardEmpty.ts";

const pollIntervalMs = 200;
const unreportedTimeoutMs = 5000;
const chartsLoadingTimeoutMs = 30000;

type WatchChartsDataEmptinessOptions = {
    origin: string;
    getWindow: () => Window | null;
    onDecision: (isEmpty: boolean | null) => void;
};

export const watchChartsDataEmptiness = ({
    origin,
    getWindow,
    onDecision,
}: WatchChartsDataEmptinessOptions): (() => void) => {
    const statusesBySource = new Map<MessageEventSource, Record<string, ChartDataStatus>>();
    const startedAt = performance.now();
    let windowSeenAt: number | null = null;
    let timeoutId: number | undefined;
    let stopped = false;

    const onMessage = (event: MessageEvent) => {
        if (event.origin !== origin || event.source === null) {
            return;
        }

        const statuses = parseChartsDataStatusMessage(event.data);
        if (statuses !== null) {
            statusesBySource.set(event.source, statuses);
        }
    };

    const stop = () => {
        stopped = true;
        window.removeEventListener("message", onMessage);
        window.clearTimeout(timeoutId);
    };

    const decide = (isEmpty: boolean | null) => {
        stop();
        onDecision(isEmpty);
    };

    const tick = () => {
        if (stopped) {
            return;
        }

        const now = performance.now();
        const target = getWindow();

        if (target !== null) {
            windowSeenAt ??= now;
            const statuses = statusesBySource.get(target) ?? null;
            const isEmpty = statuses === null ? null : isDashboardEmpty(statuses);
            if (isEmpty !== null) {
                decide(isEmpty);
                return;
            }

            if (statuses === null && now - windowSeenAt >= unreportedTimeoutMs) {
                decide(null);
                return;
            }

            target.postMessage({ type: `${chartsDataStatusMessageType}:request` }, origin);
        }

        if (now - startedAt >= chartsLoadingTimeoutMs) {
            decide(null);
            return;
        }

        timeoutId = window.setTimeout(tick, pollIntervalMs);
    };

    window.addEventListener("message", onMessage);
    tick();

    return stop;
};
